import { Injectable } from '@angular/core';
import { FilterChatSearchE } from '@enums/filter-chat-search.enum';
import { ChatFilterI } from '@interfaces/chat-filter.interface';
import { ChatFilterPreferencesI, UserPreferencesI } from '@interfaces/user-preferences.interface';
import { UserHelperService } from './user-helper.service';
import { AclAbilityService } from '@services/acl/acl.ability';
import { PermissionType, ProfileLevel, ResourceNames } from '@enums/acl/resources.enum';
import { UserAttributesI, UserI } from '@interfaces/user.interface';
import { ChatI } from '@interfaces/chat.interface';
import { ChatStoreService } from '@modules/chat/store/chat-store.service';
import { ContactTagI } from '@interfaces/contact.interface';
import { ChatStatusE } from '@enums/chat-status.enum';
import { ChatReadStatus } from '@enums/chat-read-status.enum';
import { regexIgnoreAccent } from './regex.helper';

@Injectable({
  providedIn: 'root',
})
export class ChatListHelperService {
  constructor(
    private userHelperService: UserHelperService,
    private aclService: AclAbilityService,
    private chatStoreService: ChatStoreService
  ) {}

  public isMoreFiltersEnabled(filters: Partial<ChatFilterI>): boolean {
    return (
      !!filters.team ||
      !!filters.otherTeam?.length ||
      !!filters.agent ||
      !!filters.channel ||
      !!filters.tag ||
      !!filters.content ||
      !!filters.email ||
      !!filters.name ||
      !!filters.protocol ||
      !!filters.telephone ||
      !!filters.startDate ||
      !!filters.endDate
    );
  }

  public createChatFiltersByUserPreferences(preferences: UserPreferencesI): ChatFilterI {
    const { chatFilters: filtersPref } = preferences;
    const searchType = filtersPref?.searchType ?? FilterChatSearchE.NAME;
    const searchValue = filtersPref?.searchValue ?? '';
    const useOtherTeam = this.canShowOtherTeams() && Array.isArray(filtersPref?.otherTeams);
    const teamOnly = useOtherTeam && (filtersPref?.otherTeams?.length || 0) > 0;

    return {
      onlyAttributed: filtersPref?.onlyAssigned ?? false,
      onlyNotAttributed: filtersPref?.onlyNotAssigned ?? false,
      onlyAttributedMe: filtersPref?.onlyAssignedMe ?? false,
      onlyNotRead: filtersPref?.onlyNotRead ?? false,
      onlyWaiting: filtersPref?.onlyWaiting ?? false,
      onlyMentions: filtersPref?.mentionsOfMe ?? false,
      team: filtersPref?.teams ?? '',
      agent: filtersPref?.agents ?? '',
      channel: filtersPref?.channels ?? '',
      tag: filtersPref?.tag ?? '',
      searchField: searchType,
      content: searchType === FilterChatSearchE.CONTENT ? searchValue : '',
      email: searchType === FilterChatSearchE.EMAIL ? searchValue : '',
      name: searchType === FilterChatSearchE.NAME ? searchValue : '',
      protocol: searchType === FilterChatSearchE.PROTOCOL ? searchValue : '',
      telephone: searchType === FilterChatSearchE.TELEPHONE ? searchValue : '',
      startDate: filtersPref?.startDate ?? '',
      endDate: filtersPref?.endDate ?? '',
      otherTeam: useOtherTeam && Array.isArray(filtersPref?.otherTeams) ? filtersPref?.otherTeams || [] : [],
      teamOnly,
    };
  }

  private isAdm() {
    const userAttributes = this.userHelperService.getUserAttributes() as UserAttributesI;
    return (userAttributes?.level ?? 0) >= ProfileLevel.HIGH;
  }

  private getUserId() {
    const userAttributes = this.userHelperService.getUserAttributes() as UserAttributesI;
    return userAttributes?.['custom:userId'] ?? '';
  }

  private canShowOtherTeams(): boolean {
    if (this.isAdm()) {
      return false;
    }
    return this.aclService.can(PermissionType.START_FROM_OTHER_TEAMS, ResourceNames.ATTENDANCE__CHATS);
  }

  public chatFilterToUserPreferences(filters: ChatFilterI): Partial<ChatFilterPreferencesI> {
    return {
      mentionsOfMe: filters.onlyMentions || false,
      onlyWaiting: filters.onlyWaiting || false,
      onlyNotRead: filters.onlyNotRead || false,
      onlyAssigned: filters.onlyAttributed || false,
      onlyNotAssigned: filters.onlyNotAttributed || false,
      onlyAssignedMe: filters.onlyAttributedMe || false,
      searchType: filters.searchField,
      searchValue:
        filters.searchField === FilterChatSearchE.NAME
          ? filters.name
          : filters.searchField === FilterChatSearchE.EMAIL
          ? filters.email
          : filters.searchField === FilterChatSearchE.PROTOCOL
          ? filters.protocol
          : filters.searchField === FilterChatSearchE.TELEPHONE
          ? filters.telephone
          : filters.content || '',
      startDate: filters.startDate || '',
      endDate: filters.endDate || '',
      tag: filters.tag || '',
      teams: filters.team || '',
      channels: filters.channel || '',
      agents: filters.agent || '',
      otherTeams: Array.isArray(filters.otherTeam) && filters.otherTeam.length > 0 ? filters.otherTeam : '',
    };
  }

  public chatFiltersDialogToUserPreferences(filters: ChatFilterI): Partial<ChatFilterPreferencesI> {
    return {
      searchType: filters.searchField,
      searchValue:
        filters.searchField === FilterChatSearchE.NAME
          ? filters.name
          : filters.searchField === FilterChatSearchE.EMAIL
          ? filters.email
          : filters.searchField === FilterChatSearchE.PROTOCOL
          ? filters.protocol
          : filters.searchField === FilterChatSearchE.TELEPHONE
          ? filters.telephone
          : filters.content || '',
      startDate: filters.startDate || '',
      endDate: filters.endDate || '',
      tag: filters.tag || '',
      teams: filters.team || '',
      channels: filters.channel || '',
      agents: filters.agent || '',
      otherTeams: Array.isArray(filters.otherTeam) && filters.otherTeam.length > 0 ? filters.otherTeam : '',
    };
  }

  public filterChat(chat: ChatI): boolean {
    const filters = this.chatStoreService.getCurrentChatFilterValues();
    const validators = [
      this.filterMessage.bind(this),
      this.filterReadStatus.bind(this),
      this.filterAssignment.bind(this),
      this.filterStatus.bind(this),
      this.filterMentions.bind(this),
      this.filterSearch.bind(this),
      this.filterTeams.bind(this),
      this.filterChannel.bind(this),
      this.filterEntryPointOrigin.bind(this),
      this.filterAgent.bind(this),
      this.filterProtocol.bind(this),
    ];

    for (const validator of validators) {
      if (!validator(filters, chat)) {
        return false;
      }
    }

    return true;
  }

  private filterMessage(filters: ChatFilterI, chat: ChatI): boolean {
    const hasContentFilter = !!filters.content;
    const hasDateFilter = !!(filters.startDate || filters.endDate);

    if (!hasContentFilter && !hasDateFilter) {
      return true;
    }

    if (hasContentFilter && hasDateFilter) {
      const contentRegex = new RegExp(filters.content, 'i');
      let startDate: string | null = null;
      let endDate: string | null = null;

      if (filters.startDate && filters.endDate) {
        startDate = new Date(filters.startDate).getTime().toString();
        endDate = new Date(filters.endDate).getTime().toString();
      } else if (filters.startDate) {
        startDate = new Date(filters.startDate).getTime().toString();
      } else if (filters.endDate) {
        endDate = new Date(filters.endDate).getTime().toString();
      }

      return !!chat.messages?.some((message) => {
        const matchesContent = contentRegex.test(message.content.message ?? '');
        const matchesDate =
          (startDate === null || startDate <= message.time) && (endDate === null || message.time <= endDate);
        return matchesContent && matchesDate;
      });
    }

    if (hasContentFilter) {
      const contentRegex = new RegExp(filters.content, 'i');
      return !!chat.messages?.some((message) => contentRegex.test(message.content.message ?? ''));
    }

    if (hasDateFilter) {
      let startDate: string | null = null;
      let endDate: string | null = null;

      if (filters.startDate && filters.endDate) {
        startDate = new Date(filters.startDate).getTime().toString();
        endDate = new Date(filters.endDate).getTime().toString();
      } else if (filters.startDate) {
        startDate = new Date(filters.startDate).getTime().toString();
      } else if (filters.endDate) {
        endDate = new Date(filters.endDate).getTime().toString();
      }

      return !!chat.messages?.some((message) => {
        return (startDate === null || startDate <= message.time) && (endDate === null || message.time <= endDate);
      });
    }

    return true;
  }

  private filterReadStatus(filters: ChatFilterI, chat: ChatI): boolean {
    if (filters.onlyNotRead) {
      const userId = this.getUserId();

      if (userId && Array.isArray(chat.supervisorReadStatus)) {
        const supervisorReadStatus = chat.supervisorReadStatus.find((status) => status._id === userId);

        if (supervisorReadStatus) {
          return (
            supervisorReadStatus.readStatus === ChatReadStatus.UNREAD ||
            (supervisorReadStatus.unreadMessagesCount ?? 0) > 0
          );
        }
      }

      return chat.readStatus === ChatReadStatus.UNREAD || (chat.unreadMessagesCount ?? 0) > 0;
    }

    return true;
  }

  private filterAssignment(filters: ChatFilterI, chat: ChatI): boolean {
    if (filters.onlyAttributedMe) {
      const userId = this.userHelperService.getUserAttributes()['custom:userId'];

      return chat.agent?._id === userId;
    } else if (filters.onlyAttributed) {
      return !!chat.agent;
    }

    return true;
  }

  private filterStatus(filters: ChatFilterI, chat: ChatI): boolean {
    if (filters.onlyWaiting) {
      return chat.status === ChatStatusE.WAITING;
    }

    return true;
  }

  private filterMentions(filters: ChatFilterI, chat: ChatI): boolean {
    if (filters.onlyMentions) {
      const userId = this.userHelperService.getUserAttributes()['custom:userId'];

      return !!chat.mentions?.filter((mention) => mention.agent?._id === userId).length;
    }

    return true;
  }

  private filterSearch(filters: ChatFilterI, chat: ChatI): boolean {
    const searchText = this.chatStoreService.searchText;

    if (!filters.name && !filters.email && !filters.telephone && !(filters.tag && filters.tag.length) && !searchText) {
      return true;
    }

    const onlyNumbers = (value: string) => value.replace(/\D+/g, '');
    const escapeEspecial = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = (value: string) => new RegExp(escapeEspecial(value), 'i');
    const regexAccent = (value: string) => regexIgnoreAccent(escapeEspecial(value));

    let name = false;
    let email = false;
    let telephone = false;
    let tag = false;
    let search = false;

    if (filters.name) {
      const contactName = regex(filters.name).test(chat.contact?.name ?? '');
      const internalName = !!chat.internalAgents.filter((internalAgent) => regex(filters.name).test(internalAgent.name))
        .length;
      name = contactName || internalName;
    }

    if (filters.email) {
      const contactEmail = regex(filters.email).test(chat.contact?.email ?? '');
      const contactEmails = !!chat.contact?.emails?.filter((email) => regex(filters.email).test(email)).length;
      const internalEmail = !!chat.internalAgents.filter((internalAgent: any) =>
        regex(filters.email).test(internalAgent.email)
      ).length;
      email = contactEmail || contactEmails || internalEmail;
    }

    if (filters.telephone && /^[\d\s()+-]+$/.test(filters.telephone)) {
      const numericString = onlyNumbers(filters.telephone);
      const contactTelephone = regex(numericString).test(chat.contact?.telephone ?? '');
      const contactTelephones = !!chat.contact?.telephones?.filter((telephone) => regex(numericString).test(telephone))
        .length;

      telephone = contactTelephone || contactTelephones;
    }

    if (filters.tag && filters.tag.length) {
      tag = !!(chat.contact?.tags as ContactTagI[] | undefined)?.some((tagItem) => {
        if (typeof tagItem.tag === 'string') {
          return tagItem.tag === filters.tag;
        }

        if (typeof tagItem.tag === 'object') {
          return tagItem.tag._id === filters.tag || tagItem.tag.name === filters.tag;
        }

        return false;
      });
    }

    if (searchText) {
      const orSearch: boolean[] = [];
      const regexSearch = regexAccent(searchText);

      orSearch.push(regexSearch.test(chat.contact?.name ?? ''));
      orSearch.push(!!chat.internalAgents.some((internal) => regexSearch.test(internal.name)));
      orSearch.push(regexSearch.test(chat.contact?.email ?? ''));
      orSearch.push(!!chat.contact?.emails?.some((email) => regexSearch.test(email)));
      orSearch.push(!!(chat.internalAgents as UserI[])?.some((internal) => regexSearch.test(internal.email)));

      // only phone chars
      if (/^[\d\s()+-]+$/.test(searchText)) {
        const numericString = onlyNumbers(searchText);
        const regexPhone = regex(numericString);

        orSearch.push(regexPhone.test(chat.contact?.telephone ?? ''));
        orSearch.push(!!chat.contact?.telephones?.some((telephone) => regexPhone.test(telephone)));
      }

      search = orSearch.some((result) => result);
    }

    return name || email || telephone || tag || search;
  }

  private filterTeams(filters: ChatFilterI, chat: ChatI): boolean {
    if (filters.team && filters.team.toString()) {
      return filters.team
        .toString()
        .split(',')
        .includes(chat.team?._id ?? '');
    }

    return true;
  }

  private filterChannel(filters: ChatFilterI, chat: ChatI): boolean {
    if (filters.channel && filters.channel.toString() && filters.channel !== 'all') {
      return filters.channel
        .toString()
        .split(',')
        .includes(chat.channel._id ?? '');
    }

    return true;
  }

  private filterEntryPointOrigin(filters: ChatFilterI, chat: ChatI): boolean {
    const origin = filters.entryPointOrigin;

    if (!origin) {
      return true;
    }

    // ctwaClid is only set by a real ad click; a boosted post counts as organic.
    const fromAd = !!(chat.entryPoint?.ctwaClid ?? chat.session360Dialog?.entryPoint?.ctwaClid);

    return origin === 'ad' ? fromAd : !fromAd;
  }

  private filterAgent(filters: ChatFilterI, chat: ChatI): boolean {
    if (filters.agent && filters.agent.toString()) {
      return filters.agent
        .toString()
        .split(',')
        .includes(chat.agent?._id ?? '');
    }

    return true;
  }

  private filterProtocol(filters: ChatFilterI, chat: ChatI): boolean {
    if (filters.protocol) {
      return chat.protocol?.protocolNumber === filters.protocol;
    }

    return true;
  }
}
